
import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, ViewChild, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable, Subject, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs';
import { TimeZoneService, Timezone } from './timezone.service';

@Component({
    selector: 'tm-timezone-picker',
    templateUrl: './timezone-picker.component.html',
    styleUrls: ['./timezone-picker.component.scss'],
    imports: [
        AsyncPipe,
        FormsModule,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        ReactiveFormsModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TmTimeZonePickerComponent),
            multi: true
        }
    ]
})
export class TmTimeZonePickerComponent implements ControlValueAccessor {

  private _value: string;
  private timezones: Timezone[] = [];
  protected isDisabled: boolean;
  protected timeZoneCtrl = new FormControl<Timezone>(null);
  protected timeZoneFilterCtrl = new FormControl('');

  protected get value() {
    return this._value;
  }

  protected set value(value) {
    if (value && value !== this.value) {
      this._value = value;
      this.onChange(value);
      this.onTouched();
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  private _filter: Subject<string> = new Subject();
  protected filteredTimezones$: Observable<Timezone[]>;

  constructor(
    private _cdr: ChangeDetectorRef,
    private timezoneService: TimeZoneService
  ) {

    this.filteredTimezones$ = this.timezoneService.query().pipe(
      map(timezones => timezones.sort((a, b) => a.value.localeCompare(b.value))),
      tap(timezones => {
        this.timezones = timezones;
        if (this._value) {
          const selectedTimezone = this.timezones.find(timezone => timezone.key === this._value);
          this.timeZoneCtrl.setValue(selectedTimezone);
        }
      }),
      switchMap(timezones =>
        this.timeZoneFilterCtrl.valueChanges.pipe(
          startWith(''),
          map(filterValue =>
            timezones.filter(timezone => timezone.value.toLowerCase().includes(filterValue.toLowerCase()))
          ),
        )
      ),
      tap(() => this._cdr.detectChanges()),
      takeUntilDestroyed()
    );

    this.timeZoneCtrl.valueChanges.pipe(
      distinctUntilChanged(),
      tap(() => {
          this.value = this.timeZoneCtrl.value?.key;
      }),
      takeUntilDestroyed()
    ).subscribe();
  }

  writeValue(value: any): void {
    this._value = value;

    if (this.timezones.length > 0) {
      const selectedTimezone = this.timezones.find(timezone => timezone.key === value);
      this.timeZoneCtrl.setValue(selectedTimezone, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  applyFilter(): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this._filter.next(filterValue);
  }
}